import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Button, Icon, Loader, Modal, notification, Title,
} from 'ui-library';
import { ICON_TYPE_BIN, ICON_TYPE_CLOSE } from 'ui-library/components/Icon';
import { useCaseDetails } from 'datasource/CaseManagement/CaseDetailsManager/CaseDetailsManager';
import './CaseDetailsHeader.css';

const CaseDetailsHeader = ({ submittedCase }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [showUploadModal, setShowUploadModal] = useState(false);
    const { dfsClientId } = useParams();
    const [isCancelInProgress, setCancelInProgress] = useState(false);

    const {
        cancelCase,
        isCaseCancelable,
        portfolioId,
    } = useCaseDetails();

    const backURL = useMemo(() => (`/client/${dfsClientId}/portfolios/${portfolioId}/tab/cases`), [dfsClientId, portfolioId]);

    const handleCancelCase = useCallback(async () => {
        try {
            setCancelInProgress(true);
            await cancelCase();

            notification.open({
                content: t('dashboard.overviewClientApplications.canceledSuccessfully'),
                type: 'success',
            });
        } catch (err) {
            notification.open({
                content: t('dashboard.overviewClientApplications.cancelError'),
                type: 'error',
            });
        } finally {
            setShowUploadModal(false);
            setCancelInProgress(false);
        }
    }, []);

    const goBackToOverview = useCallback(() => {
        if (dfsClientId && portfolioId) {
            navigate(backURL);
        }
    }, [dfsClientId, portfolioId]);

    return (
        <>
            <div className="application-details-header">
                <div>
                    <Button
                        type="link"
                        onClick={goBackToOverview}
                    >
                        <Icon size={16} className="mr-10" type="arrow-left"/>
                        {t('ApplicationDetails.Button.GoToCaseOverview')}
                    </Button>
                </div>

                <div>
                    <Title type="1" className="application-details-header--title font-normal">
                        {submittedCase ? 'Case' : t('applicationDetailsHeader.caseOrders')}
                    </Title>
                </div>

                {!submittedCase && isCaseCancelable && (
                    <div className="flex flex-end">
                        <Button
                            className="application-details-header--delete-btn"
                            type="link"
                            onClick={() => setShowUploadModal(true)}
                        >
                            <Icon size={16} className="mr-10" type={ICON_TYPE_BIN}/>
                            {t('applicationDetailsHeader.Delete')}
                        </Button>
                    </div>
                )}
            </div>
            {showUploadModal && (
                <Modal
                    title=""
                    visible
                    okText={isCancelInProgress ? <Loader/> : t('clientDashboard.delete')}
                    cancelText={t('clientDashboard.cancel')}
                    onOk={handleCancelCase}
                    okButtonProps={{ disabled: false }}
                    cancelButtonProps={{ disabled: false }}
                    onCancel={() => setShowUploadModal(false)}
                    width={512}
                    className="delete-application-modal"
                    borderRadius={24}
                    closable
                    closeIcon={<Icon type={ICON_TYPE_CLOSE} size={16}/>}
                >
                    <Title type={3} className="font-normal">
                        {t('caseDetailsHeader.deleteCase.Confirmation')}
                    </Title>
                </Modal>
            )}
        </>
    );
};

export default CaseDetailsHeader;
